import React, { useEffect, useRef, useState } from 'react';
import {
  Checkbox,
  Form,
  Icon,
  Input,
  Message,
  Modal,
  Table
} from 'semantic-ui-react';

import Button from '../../../components/Button';
import { maskMoney } from '../../../services/masks';
import { StandaloneChargeDTO } from '../../../services/standalone-charges';
import { FormatCurrency, UnMaskCurrency } from '../../../util';
import { ChargeRefundData } from '../types';

type Props = {
  open: boolean;
  charge: StandaloneChargeDTO | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (data: ChargeRefundData) => void;
};

const sectionHeaderStyle: React.CSSProperties = {
  color: '#5B21B6',
  borderBottom: '1px solid #D8B4FE',
  paddingBottom: 8,
  marginBottom: 16,
  fontWeight: 700
};

const formatCurrency = (amount?: number): string =>
  `R$ ${maskMoney(String(Number(amount || 0).toFixed(2)), true)}`;

const allowedAttachmentMimeTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/pdf',
  'application/vnd.oasis.opendocument.text'
];

const acceptedAttachmentTypes = '.jpg,.jpeg,.png,.webp,.doc,.pdf,.odt';

const RefundChargeModal: React.FC<Props> = ({
  open,
  charge,
  loading = false,
  onClose,
  onConfirm
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [refundAll, setRefundAll] = useState(false);
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState('');

  const totalAmount = Number(charge?.amount || 0);
  const alreadyRefundedAmount = Number(charge?.refundedAmount || 0);
  const availableRefundAmount = Math.max(totalAmount - alreadyRefundedAmount, 0);
  const amountIsInvalid = amount <= 0 || amount > availableRefundAmount;
  const attachmentIsInvalid = attachments.length === 0;
  const canConfirm =
    Boolean(charge) &&
    !amountIsInvalid &&
    description.trim().length > 0 &&
    !attachmentIsInvalid;

  useEffect(() => {
    if (!open) {
      setRefundAll(false);
      setAmount(0);
      setDescription('');
      setAttachments([]);
      setAttachmentError('');
      return;
    }

    if (charge && refundAll) {
      setAmount(availableRefundAmount);
    }
  }, [availableRefundAmount, charge, open, refundAll]);

  const handleToggleRefundAll = (_: unknown, data: { checked?: boolean }) => {
    const checked = Boolean(data.checked);
    setRefundAll(checked);
    setAmount(checked ? availableRefundAmount : 0);
  };

  const handleConfirm = () => {
    if (!charge || !canConfirm) return;
    onConfirm({
      chargeId: charge.id,
      amount,
      description: description.trim(),
      refundedAt: new Date().toISOString(),
      attachments
    });
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const acceptedFiles = files.filter(file =>
      allowedAttachmentMimeTypes.includes(file.type)
    );
    const rejectedFiles = files.filter(file =>
      !allowedAttachmentMimeTypes.includes(file.type)
    );

    if (rejectedFiles.length > 0) {
      setAttachmentError(
        `Arquivo não permitido: ${rejectedFiles.map(file => file.name).join(', ')}. Envie JPG, PNG, WebP, DOC, PDF ou ODT.`
      );
    } else {
      setAttachmentError('');
    }

    if (acceptedFiles.length > 0) {
      setAttachments(current => [...current, ...acceptedFiles]);
    }
    event.target.value = '';
  };

  const handleDownloadAttachment = (file: File) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const handleRemoveAttachment = (fileToRemove: File) => {
    setAttachments(current =>
      current.filter(file => file !== fileToRemove)
    );
    setAttachmentError('');
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeOnDimmerClick={false}>
      <Modal.Header>
        <Icon name="tag" style={{ color: '#5B21B6' }} />
        Estornar cobrança
      </Modal.Header>
      <Modal.Content>
        <Form>
          <div style={sectionHeaderStyle}>Detalhes do estorno</div>
          <Form.Group widths="equal">
            <Form.Field required error={amountIsInvalid && amount > 0}>
              <label>Valor estornado</label>
              <Input
                placeholder="Digite..."
                value={amount ? FormatCurrency(amount) : ''}
                disabled={refundAll}
                onChange={(_, { value }) => setAmount(UnMaskCurrency(String(value || '')))}
              />
              <div style={{ marginTop: 8, fontSize: 12, color: '#6B7280' }}>
                Total recebido: {formatCurrency(totalAmount)}
                {alreadyRefundedAmount > 0 && (
                  <>
                    {' | '}
                    Já estornado: {formatCurrency(alreadyRefundedAmount)}
                    {' | '}
                    Saldo disponível: {formatCurrency(availableRefundAmount)}
                  </>
                )}
              </div>
            </Form.Field>
            <Form.Field style={{ display: 'flex', alignItems: 'center', paddingTop: 24 }}>
              <Checkbox
                label="Estornar valor total"
                checked={refundAll}
                onChange={handleToggleRefundAll}
              />
            </Form.Field>
          </Form.Group>

          {amountIsInvalid && amount > 0 && (
            <Message warning size="small">
              Informe um valor maior que zero e menor ou igual ao saldo disponível.
            </Message>
          )}

          <Form.TextArea
            required
            label="Descrição"
            placeholder="Ex: Motivo do estorno é..."
            value={description}
            onChange={(_, data) => setDescription(String(data.value || ''))}
          />

          <div style={{ ...sectionHeaderStyle, marginTop: 24 }}>Anexos *</div>
          {attachmentError && (
            <Message negative size="small">
              {attachmentError}
            </Message>
          )}
          <Table celled compact unstackable>
            <Table.Header>
              <Table.Row style={{ background: '#EDE3FF' }}>
                <Table.HeaderCell>
                  <Icon name="file outline" style={{ color: '#5B21B6' }} />
                  Arquivos
                </Table.HeaderCell>
                <Table.HeaderCell textAlign="center" width={3}>
                  Ações
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {attachments.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={2} textAlign="center">
                    <div style={{ color: '#9F3A38' }}>
                      <Icon name="file outline" />
                      Anexe ao menos um arquivo para confirmar o estorno
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                attachments.map(file => (
                  <Table.Row key={`${file.name}-${file.size}-${file.lastModified}`}>
                    <Table.Cell>
                      <Icon name="file outline" style={{ color: '#5B21B6' }} />
                      {file.name}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Button
                        type="button"
                        color="blue"
                        icon="download"
                        size="mini"
                        style={{ marginRight: 6 }}
                        aria-label={`Baixar ${file.name}`}
                        onClick={() => handleDownloadAttachment(file)}
                      />
                      <Button
                        type="button"
                        color="red"
                        icon="trash"
                        size="mini"
                        aria-label={`Remover ${file.name}`}
                        onClick={() => handleRemoveAttachment(file)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
              <Table.Row>
                <Table.Cell colSpan={2} textAlign="center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={acceptedAttachmentTypes}
                    style={{ display: 'none' }}
                    onChange={handleFilesSelected}
                  />
                  <Button
                    type="button"
                    basic
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Icon name="plus circle" />
                    Adicionar arquivo
                  </Button>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" color="grey" onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          type="button"
          color="green"
          loading={loading}
          disabled={!canConfirm || loading}
          onClick={handleConfirm}
        >
          Confirmar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default RefundChargeModal;
